import { Injectable } from '@angular/core';
import {
	ColumnModel,
	DataModel,
	fileBooleanMappings,
	formatDateDbToForm,
	formatDateTimeDbToForm,
	listToDictionaryObject,
	getPrimaryKeys,
	PrimaryKeyValueModel,
	ReferenceTableModel
} from '@shared';
import { first, map, switchMap } from 'rxjs/operators';
import { combineLatest, Observable, of } from 'rxjs';
import {
	EditStyleModel,
	FormColumnCustomOptions,
	FormControlCustomOptions,
	FormControlTypeEnum,
	FormValueModel
} from '../models';
import { DataRepository, WorkspaceDatabaseKeys } from '@database/workspace';
import { ColumnDataTypes } from '@database/models';
import { FormControlTypeProviderService } from '../factories';
import { DropdownDataSourceProvider } from './dropdown-data-source.provider';
import { SamplesService, SampleTypeProvider } from '@domain/business-layer';
import { TableTypeProvider } from '../../business-layer/services/table-type.provider';
import { QueryParamsProvider } from '../../routing/services/query-params.provider';
import { SignatureColumn } from '../models/signature-column.enum';

@Injectable()
export class NormalizeDatabaseValuesService {
	public constructor(
		private dataRepository: DataRepository,
		private formControlTypeProviderService: FormControlTypeProviderService,
		private dropdownDataSource: DropdownDataSourceProvider,
		private sampleTypeProvider: SampleTypeProvider,
		private samplesService: SamplesService,
		private tableTypeProvider: TableTypeProvider,
		private queryParamsProvider: QueryParamsProvider
	) {}

	public prepareEditFormValues$(
		columns: ColumnModel[],
		tableName: string,
		projectNumber: string,
		entryId: number,
		primaryKeys: string[]
	): Observable<{ formValueModel: FormValueModel; controlCustomOptions: FormColumnCustomOptions }> {
		return this.dataRepository.getTableEntry$(tableName, projectNumber, entryId).pipe(
			switchMap((row: DataModel) =>
				this.sampleTypeProvider.isQcSampleEntry$().pipe(
					map((isQcSample: boolean) => {
						const dataColumnsDictionary = listToDictionaryObject(
							WorkspaceDatabaseKeys.columns.columnName,
							columns
						);
						const readonlyFields = [
							WorkspaceDatabaseKeys.columns.depthFrom,
							WorkspaceDatabaseKeys.columns.depthTo
						];

						const tableEntry: FormValueModel = {};
						const columnCustomOptions: FormControlCustomOptions = { disabled: true };
						const controlCustomOptions: FormColumnCustomOptions = {};

						Object.entries(row).forEach(([fieldKey, fieldValue]) => {
							let column: ColumnModel;
							if (dataColumnsDictionary[fieldKey]) {
								column = dataColumnsDictionary[fieldKey];

								tableEntry[fieldKey] = fieldValue;

								if (
									(isQcSample && readonlyFields.includes(fieldKey)) ||
									primaryKeys.includes(fieldKey)
								) {
									controlCustomOptions[fieldKey] = columnCustomOptions;
								}

								tableEntry[fieldKey] = this.prepareFormValue(column, fieldValue);
							}
						});

						return { formValueModel: tableEntry, controlCustomOptions };
					})
				)
			)
		);
	}

	public prepareDupFormValues$(
		columns: ColumnModel[],
		tableName: string,
		projectNumber: string,
		entryId: number,
		primaryKeyValue: PrimaryKeyValueModel[]
	): Observable<{ formValueModel: FormValueModel; controlCustomOptions: FormColumnCustomOptions }> {
		// todo: use TableTypeProvider.getTableType()
		return this.tableTypeProvider
			.isFlatSamplesTable$(this.queryParamsProvider.workfileId, this.queryParamsProvider.parentTableName)
			.pipe(
				first(),
				switchMap((isFlatSamplesTable: boolean) =>
					this.dataRepository.getTableEntry$(tableName, projectNumber, entryId).pipe(
						switchMap((row: DataModel) =>
							combineLatest([
								of(row),
								this.dropdownDataSource.getReferenceTablesByCategory$(true),
								this.samplesService.getNewSampleNumber$(projectNumber, primaryKeyValue)
							]).pipe(
								map((res: [DataModel, ReferenceTableModel[], string]) => {
									const combineRes = {
										row: res[0],
										qcRefValues: res[1],
										newSampleNumber: res[2]
									};
									return combineRes;
								})
							)
						),
						map((data: { row: DataModel; qcRefValues: ReferenceTableModel[]; newSampleNumber: string }) => {
							const dataColumnsDictionary = listToDictionaryObject(
								WorkspaceDatabaseKeys.columns.columnName,
								columns
							);
							let preFilledFields = [
								WorkspaceDatabaseKeys.columns.depthFrom,
								WorkspaceDatabaseKeys.columns.depthTo
							];

							// todo: use TableTypeProvider.getTableType()
							if (!isFlatSamplesTable) {
								preFilledFields = preFilledFields.concat(getPrimaryKeys(primaryKeyValue));
							}

							const tableEntry: FormValueModel = {};
							let columnCustomOptions: FormControlCustomOptions = {};
							const controlCustomOptions: FormColumnCustomOptions = {};

							Object.entries(data.row).forEach(([fieldKey, fieldValue]) => {
								let column: ColumnModel;

								// We use data.qcRefValues[0], because sample type values will be received alphabetically sorted and
								// the first available option is set in the form
								if (
									fieldKey === WorkspaceDatabaseKeys.referenceTables.assaySampleTypeCode &&
									data.qcRefValues[0]
								) {
									tableEntry[fieldKey] =
										data.qcRefValues[0][WorkspaceDatabaseKeys.referenceTables.assaySampleTypeCode];
								}

								if (fieldKey === WorkspaceDatabaseKeys.columns.sampleNumber && data.newSampleNumber) {
									tableEntry[fieldKey] = data.newSampleNumber;
								}

								if (dataColumnsDictionary[fieldKey] && preFilledFields.includes(fieldKey)) {
									column = dataColumnsDictionary[fieldKey];

									tableEntry[fieldKey] = fieldValue;

									const fieldPrimaryKey: PrimaryKeyValueModel | undefined = primaryKeyValue.find(
										(item: PrimaryKeyValueModel) => item.primaryKey === fieldKey
									);
									if (fieldPrimaryKey) {
										tableEntry[fieldKey] = fieldPrimaryKey.primaryKeyValue;
									}

									columnCustomOptions = { disabled: true };
									controlCustomOptions[fieldKey] = columnCustomOptions;

									tableEntry[fieldKey] = this.prepareFormValue(column, fieldValue);
								}
							});

							return { formValueModel: tableEntry, controlCustomOptions };
						})
					)
				)
			);
	}

	public prepareNewFromValues$(
		columns: ColumnModel[],
		tableName: string,
		projectNumber: string,
		entryId: number
	): Observable<{ formValueModel: FormValueModel; controlCustomOptions: FormColumnCustomOptions }> {
		// todo: use TableTypeProvider.getTableType()
		return this.dataRepository.getTableEntry$(tableName, projectNumber, entryId).pipe(
			map((row: DataModel) => {
				const dataColumnsDictionary = listToDictionaryObject(WorkspaceDatabaseKeys.columns.columnName, columns);
				const notFilledFields = [WorkspaceDatabaseKeys.columns.sampleNumber];

				const tableEntry: FormValueModel = {};
				const controlCustomOptions: FormColumnCustomOptions = {};

				Object.entries(row).forEach(([fieldKey, fieldValue]) => {
					let column: ColumnModel;
					if (dataColumnsDictionary[fieldKey]) {
						column = dataColumnsDictionary[fieldKey];

						if (!notFilledFields.includes(fieldKey)) {
							tableEntry[fieldKey] = fieldValue;
						}

						tableEntry[fieldKey] = this.prepareFormValue(column, fieldValue);
					}
				});
				return { formValueModel: tableEntry, controlCustomOptions };
			})
		);
	}

	public prepareFormValue(column: Partial<ColumnModel>, fieldValue: any): string | boolean | number | null {
		if (fieldValue !== undefined && fieldValue !== null) {
			switch (column.data_type) {
				case ColumnDataTypes.NUMERIC:
					return String(fieldValue);
				case ColumnDataTypes.DATETIME: {
					if (
						column.edit_style &&
						(column.edit_style.toLowerCase() === EditStyleModel.time24 ||
							column.edit_style.toLowerCase() === EditStyleModel.timeAmPm)
					) {
						return fieldValue;
					}

					return formatDateTimeDbToForm(fieldValue);
				}
				case ColumnDataTypes.IMAGE: {
					// We currently do not support any other cases than Signatures, meaning that any Image will be a Signature
					return !fieldValue ? null : SignatureColumn.IsPreviouslySignedValue;
				}
				case ColumnDataTypes.DATE:
					return formatDateDbToForm(fieldValue);
				case ColumnDataTypes.CHAR:
					if (
						this.formControlTypeProviderService.getFormControlType(column) === FormControlTypeEnum.CheckBox
					) {
						return fileBooleanMappings[fieldValue];
					}
					break;
				default:
					return fieldValue;
			}
		}

		return fieldValue;
	}
}
